import React from "react";
import feature1 from "./Images/feature1.jpg";
import feature2 from "./Images/feature2.jpg";
import feature3 from "./Images/feature3.jpg";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Zap, ShoppingBag, ShieldCheck } from "lucide-react";

const features = [
  {
    id: 1,
    img: feature1,
    icon: <Zap size={20} />,
    title: "Smart Shopping",
    desc: "Daily Go brings essentials, groceries, and gadgets right to your door. Experience modern living with lightning-fast logistics.",
  },
  {
    id: 2,
    img: feature2,
    icon: <ShoppingBag size={20} />,
    title: "All-in-One Marketplace",
    desc: "From fresh produce to tech must-haves, we deliver variety and speed. Your go-to digital marketplace designed for you.",
  },
  {
    id: 3,
    img: feature3,
    icon: <ShieldCheck size={20} />,
    title: "Trusted Service",
    desc: "Timely, secure, and hassle-free. With real-time tracking and smart logistics, spend less time waiting and more time living.",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function FeaturesSection() {
  return (
    <section className="bg-[#fafafa] py-24 px-6">
      
      {/* HEADER */}
      <div className="text-center mb-16 max-w-2xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-2 mb-3"
        >
          <span className="w-8 h-px bg-yellow-500"></span>
          <span className="text-yellow-500 font-semibold uppercase tracking-widest text-xs">
            Why Choose Us
          </span>
          <span className="w-8 h-px bg-yellow-500"></span>
        </motion.div>
        
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-3xl md:text-4xl font-extrabold text-gray-900"
        >
          Everything You Need, <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-400">
            Just a Tap Away.
          </span>
        </motion.h2>
      </div>

      {/* GRID */}
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="max-w-7xl mx-auto grid gap-8 md:grid-cols-3"
      >
        {features.map((item) => (
          <motion.div
            key={item.id}
            variants={cardVariants}
            className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col"
          >
            {/* IMAGE */}
            <div className="relative h-56 overflow-hidden">
              <img
                src={item.img}
                alt={item.title}
                className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
              />

              {/* ICON BADGE */}
              <div className="absolute bottom-4 right-4 bg-yellow-500 text-black p-3 rounded-xl shadow-md opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                {item.icon}
              </div>
            </div>

            {/* CONTENT */}
            <div className="p-6 md:p-8 flex flex-col flex-grow">
              
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-yellow-500 transition">
                {item.title}
              </h3>
              
              <p className="text-gray-600 text-sm leading-relaxed flex-grow mb-6">
                {item.desc}
              </p>
              
              {/* CTA */}
              <Link
                to="/ServiceIndex"
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 group/btn"
              >
                <span className="border-b border-yellow-500 pb-0.5 group-hover/btn:text-yellow-500 transition">
                  View Details
                </span>
                <ArrowRight size={16} className="text-yellow-500 group-hover/btn:translate-x-1 transition" />
              </Link>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}